import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "."
import { OemDevice } from "@/types/oem-device"
import moment from "moment"

export interface OemDeviceControllersState {
    loading: boolean
    locationId: number | null
    controllers: OemDevice[]
}

export interface ControllersApiResponse {
    data: {
        data: {
            controllers: OemDevice[]
        }
    }
}

const state: OemDeviceControllersState = {
    loading: false,
    locationId: null,
    controllers: [],
}

const mutations: MutationTree<OemDeviceControllersState> = {
    setLoading(state, value: boolean) {
        state.loading = value
    },
    setLocationId(state, id: number | null) {
        state.locationId = id
    },
    setControllers(state, controllers: OemDevice[]) {
        state.controllers = controllers
    },
    markControllerUpdating(state, controllerId: number) {
        const index = state.controllers.findIndex((c) => c.id === controllerId)
        if (index !== -1) {
            state.controllers = state.controllers.map((controller) =>
                controller.id === controllerId
                    ? { ...controller, updateStartedAt: new Date() as any }
                    : controller,
            )
        }
    },
}

const actions: ActionTree<OemDeviceControllersState, RootState> = {
    markControllerUpdating({ commit }, controllerId: number) {
        commit("markControllerUpdating", controllerId)
    },
    async fetchControllers({ commit }, locationId: number) {
        commit("setLoading", true)
        commit("setLocationId", locationId)

        try {
            const url = "/locations/" + locationId + "/controllers"
            const response: ControllersApiResponse = await axios.get(url)
            commit("setControllers", response.data.data.controllers)
        } catch (error) {
            console.log("Failed to fetch controllers:", error)
        } finally {
            commit("setLoading", false)
        }
    },
}

const getters: GetterTree<OemDeviceControllersState, RootState> = {
    controllers: (state) => state.controllers,
    loading: (state) => state.loading,
    isControllerUpdating: (state) => (controllerId: number) => {
        const controller = state.controllers.find((c) => c.id === controllerId)
        if (!controller) return false

        if (controller.updateStartedAt === null) return false

        const updateStartedAt = moment.utc(controller.updateStartedAt)
        return moment.duration(moment().utc().diff(updateStartedAt)).asMinutes() < 15
    },
    hasUpdatingControllers: (state) => {
        return state.controllers.some((controller) => {
            if (controller.updateStartedAt === null) return false

            const updateStartedAt = moment.utc(controller.updateStartedAt)
            return moment.duration(moment().utc().diff(updateStartedAt)).asMinutes() < 15
        })
    },
}

const oemDeviceControllersModule: Module<OemDeviceControllersState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default oemDeviceControllersModule
